/**
 *
 * Tickets
 *
 */

import React, { memo, useEffect } from 'react';

import { TicketSyncScope } from '@ariksa/notification/api';
import { Box, HStack, Stack } from '@chakra-ui/react';
import { useDispatch, useSelector } from 'react-redux';

import { Card, PageHeaderWithIcon } from 'components/DataDisplay';
import { BorderedBox } from 'components/DataDisplay/Box/BorderedBox';
import { LearnMore } from 'components/DataDisplay/KnowledgeBase/Components/LearnMore';
import { useKnowledgeBase } from 'components/DataDisplay/KnowledgeBase/hook/useKnowledgeBase';
import { LearnMoreButton } from 'components/DataEntry/Button/LearnMoreButton';
import { OutlinedIconButton } from 'components/DataEntry/Button/OutlinedIconButton';
import { RefreshIcon, TicketIcon } from 'components/Icons';
import { ActiveResourceProvider } from 'containers/ActiveCloudResource/hooks/context/ActiveResourceProvider';
import { TicketsContextProvider } from 'containers/Findings/Tickets/Components/hooks/context';
import { useTickets } from 'containers/Findings/Tickets/Components/hooks/useTickets';
import { TicketsTable } from 'containers/Findings/Tickets/Components/TicketsTable';
import { TicketTableFilters } from 'containers/Findings/Tickets/Components/TicketTableFilters';
import { ViewTicketsBy } from 'containers/Findings/Tickets/Components/ViewTicketsBy';
import { ticketSaga } from 'containers/Findings/Tickets/saga';
import { selectTickets } from 'containers/Findings/Tickets/selectors';
import { actions, reducer, sliceKey } from 'containers/Findings/Tickets/slice';
import { actions as sharedStateActions } from 'containers/SharedState/slice';
import { useInjector } from 'utils/inject';

export const Tickets = memo(() => {
  useInjector(sliceKey, reducer, ticketSaga);

  const dispatch = useDispatch();
  const { syncAllTicketsInformation } = useSelector(selectTickets);
  const { setLearnMore } = useKnowledgeBase();

  useEffect(() => {
    dispatch(sharedStateActions.getAlertResourceTypes({ q: {} }));
  }, [dispatch]);

  return (
    <Card styles={{ card: { h: 'full' } }}>
      <TicketsContextProvider value={useTickets()}>
        <Stack w="full" h="full" spacing={5}>
          <HStack justify="space-between" w="full">
            <Box>
              <PageHeaderWithIcon label="Tickets" icon={<TicketIcon />} />
            </Box>
            <HStack>
              <LearnMoreButton
                onOpen={() => setLearnMore('ticketing')}
                label="Ticketing"
              />
              <OutlinedIconButton
                leftIcon={<RefreshIcon />}
                onClick={() => {
                  dispatch(
                    actions.syncAllTicketsInformation({
                      q: {
                        allTicketsSyncRequest: {
                          scope: TicketSyncScope.All,
                        },
                      },
                    }),
                  );
                }}
                isLoading={syncAllTicketsInformation.isLoading}
                tooltip="Sync all ticket status"
                tooltipStyles={{ tooltip: { w: 24, textAlign: 'center' } }}
              >
                Sync All
              </OutlinedIconButton>
            </HStack>
          </HStack>
          <ViewTicketsBy />
          <BorderedBox h="full">
            <Stack h="full" spacing={5}>
              <TicketTableFilters />
              <ActiveResourceProvider>
                <Box flex={1} h="full">
                  <TicketsTable />
                </Box>
              </ActiveResourceProvider>
            </Stack>
          </BorderedBox>
        </Stack>
      </TicketsContextProvider>
      <LearnMore />
    </Card>
  );
});
